"use client";

import { Button } from "../../../../shadcn/ui/button";
import { Switch } from "../../../../shadcn/ui/switch";
import { Label } from "../../../../shadcn/ui/label";
import { ArrowLeft, Calendar, MapPin, Users, DollarSign, Check, Rocket, RotateCcw, Eye, EyeOff } from "lucide-react";
import { useParams } from "react-router-dom";
import type { ListingData } from "./index";
import SaveStatusIndicator from "./SaveStatusIndicator";
import { useState, useEffect } from "react";
import * as localService from "../../../../services/localService";
import amenitiesService from "../../../../services/amenitiesService";
import addonService from "../../../../services/addonService";
import billingService from "../../../../services/billingService";
import policyManagementService from "../../../../services/policyManagementService";
import { buildingAddressService } from "../../../../services/buildingAddressService";
import logger from "../../../../utils/logger";


interface StepFourProps {
  data: ListingData;
  updateData: (data: Partial<ListingData>) => void;
  onPrev: () => void;
  publishing: boolean;
  onPublish: () => Promise<void>;
  lastSaved: Date | null;
  onClearForm: () => void;
  isEditMode?: boolean;
  onTogglePublish?: () => void;
}

export default function StepFour({
  data,
  updateData,
  onPrev,
  publishing,
  onPublish,
  lastSaved,
  onClearForm,
  isEditMode,
  onTogglePublish,
}: StepFourProps) {
  const params: any = useParams();
  const spaceId = isEditMode ? Number(params.id) : undefined;
  const [amenitiesList, setAmenitiesList] = useState<any[]>([]);
  const [addonsList, setAddonsList] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [subcategoryName, setSubcategoryName] = useState<string>("");
  const [selectedPolicy, setSelectedPolicy] = useState<any | null>(null);
  const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
  const [buildingAddressLine, setBuildingAddressLine] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoadingDetails(true);

        // Fetch amenities
        if (data.amenities && data.amenities.length > 0) {
          const amenitiesRes = await amenitiesService.getAllAmenities();
          const allAmenities = amenitiesRes?.data?.content || [];
          const selectedAmenities = allAmenities.filter((a: any) => data.amenities?.includes(a.id));
          setAmenitiesList(selectedAmenities);
        }

        // Fetch addons
        if (data.addons && data.addons.length > 0) {
          const addonsRes = await addonService.getMyOrganizationAddons({ size: 100 });
          const allAddons = addonsRes?.data?.content || [];
          const selectedAddons = allAddons.filter((a: any) => data.addons?.includes(a.id));
          setAddonsList(selectedAddons);
        }

        // Fetch cancellation policy name/description
        if (typeof data.cancellationPolicy === "number") {
          try {
            const policiesRes = await policyManagementService.getCancellationPolicies(0, 50);
            const list = policiesRes?.content || [];
            const match = list.find((p: any) => p.id === data.cancellationPolicy);
            if (match) setSelectedPolicy(match);
          } catch (e) {
            // ignore
          }
        }

        // Fetch terms titles
        if (Array.isArray(data.termsIds) && data.termsIds.length > 0) {
          try {
            const termsRes = await policyManagementService.getTermsAndConditions(0, 100);
            const list = termsRes?.content || [];
            const match = list.filter((t: any) => data.termsIds?.includes(t.id));
            setSelectedTerms(match);
          } catch (e) {
            // ignore
          }
        }

        // Fetch subcategories to resolve selected subcategory name for badge
        if (data.subCategoryId) {
          try {
            const resp = await billingService.getServiceSubcategories(3);
            const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp?.data?.data) ? resp.data.data : [];
            const match = list.find((s: any) => s.id === data.subCategoryId);
            if (match?.name) setSubcategoryName(match.name);
          } catch (e) {
            // ignore
          }
        }
      } catch (error) {
        logger.error("Error fetching details:", error);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [data.amenities, data.addons, data.subCategoryId, data.cancellationPolicy, data.termsIds]);

  // Resolve building address line for preview (street address only)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setBuildingAddressLine("");
        if (!data.buildingAddressId) return;
        const resp = await buildingAddressService.getBuildingAddressById(data.buildingAddressId as any);
        if (cancelled) return;
        const addr = resp?.data;
        if (addr && typeof addr.streetAddress === "string" && addr.streetAddress.trim().length > 0) {
          setBuildingAddressLine(addr.streetAddress);
        }
      } catch (_e) {
        // ignore preview address failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data.buildingAddressId]);

  const handleDelete = async () => {
    if (!isEditMode || !spaceId) return;
    try {
      setDeleting(true);
      const mod = await import("../../../../services/host-apiServices/spaceManagementService");
      const localService = await import("../../../../services/localService");
      const currentUser = localService.getUser();
      if (!currentUser) {
        throw new Error("User not found");
      }
      await mod.spaceManagementService.deleteSpace(spaceId, currentUser.id);
      const toastUtils = await import("../../../../utils/toast");
      toastUtils.default.success("Play service deleted");
      setTimeout(() => {
        window.location.href = "/host/spaces?type=play";
      }, 1000);
    } catch (e) {
      const messageHandler = await import("../../../../utils/messageHandler");
      messageHandler.showApiMessage(e, "delete play service");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatPrice = (price: number, duration?: string) => {
    if (!price) return "";
    const durationText = {
      HOURLY: "per hour",
      DAILY: "per day",
      WEEKLY: "per week",
      MONTHLY: "per month",
    };
    // Currency should always be set from user's country - no KES fallback
    return `${data.currency || ""} ${price} ${durationText[duration as keyof typeof durationText] || ""}`.trim();
  };

  const handlePublish = () => {
    onPublish();
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onPrev}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Add-ons
            </button>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Step 4 of 4</span>
              <SaveStatusIndicator lastSaved={lastSaved} />
              {isEditMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  {...({} as any)}
                >
                  Delete
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={onClearForm} className="gap-2" {...({} as any)}>
                  <RotateCcw className="h-4 w-4" />
                  Start Over
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <h1 className="mb-2 text-2xl font-semibold">
            {isEditMode
              ? `Edit: ${data.location
                  ? `${data.location} - ${localService.getUser()?.organization?.name || localService.getUser()?.organizationName || "Host"}`
                  : data.title || "Play Service"}`
              : "Create New Play Service"}
          </h1>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full w-full bg-foreground transition-all" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Preview & Publish</span>
            <span className="text-muted-foreground">100% Complete</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-12 text-center">
          <h2 className="mb-2 text-3xl font-semibold">{isEditMode ? "Review your changes" : "Almost there!"}</h2>
          <p className="text-muted-foreground">
            {isEditMode
              ? "Check the details before saving updates"
              : "Review your play service and publish when you're ready"}
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Preview Card */}
          <div className="lg:col-span-2">
            <h3 className="mb-4 text-xl font-semibold">Preview Your Play Service</h3>
            <p className="mb-6 text-sm text-muted-foreground">This is how participants will see your experience</p>

            <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
              {/* Image */}
              <div className="relative h-64 bg-gradient-to-br from-blue-400 to-blue-600">
                {data.uploadedImages && data.uploadedImages.length > 0 ? (
                  <img
                    src={data.uploadedImages[0].url}
                    alt="Experience preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600">
                    <div className="text-center text-white">
                      <div className="mb-2 text-4xl">📸</div>
                      <div className="text-sm">No images uploaded</div>
                    </div>
                  </div>
                )}
                {subcategoryName && (
                  <div className="absolute left-4 top-4">
                    <span className="rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white capitalize">
                      {subcategoryName}
                    </span>
                  </div>
                )}
                {data.uploadedImages && data.uploadedImages.length > 1 && (
                  <div className="absolute right-4 top-4">
                    <span className="rounded-full bg-black/50 px-3 py-1 text-sm font-semibold text-white">
                      +{data.uploadedImages.length - 1} more
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h2 className="mb-2 text-2xl font-semibold">
                  {data.hostDisplayName || data.location || data.title || subcategoryName || "Untitled Service"}
                </h2>
                <p className="mb-6 text-muted-foreground">{data.description || "No description provided"}</p>

                {/* Details Grid */}
                <div className="mb-6 grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="mt-1 h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Date & Time</div>
                      <div className="text-sm text-muted-foreground">
                        {data.dateTime ? new Date(data.dateTime).toLocaleString() : "Not set"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-1 h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Location</div>
                      <div className="text-sm text-muted-foreground">{buildingAddressLine || "Not set"}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="mt-1 h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Capacity</div>
                      <div className="text-sm text-muted-foreground">
                        {data.capacity ? `Up to ${data.capacity} participants` : "Not set"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="mt-1 h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Price</div>
                      <div className="text-sm text-muted-foreground">
                        {data.servicePrice && data.currency
                          ? `${data.currency} ${data.servicePrice}`
                          : data.servicePrice
                            ? data.servicePrice
                            : "Not set"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 text-muted-foreground">⏱️</div>
                    <div>
                      <div className="text-sm font-semibold">Duration Type</div>
                      <div className="text-sm text-muted-foreground">{data.duration || "Not set"}</div>
                    </div>
                  </div>
                  {/* <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 text-muted-foreground">🏷️</div>
                    <div>
                      <div className="text-sm font-semibold">Space Tier</div>
                      <div className="text-sm text-muted-foreground">
                        Low Tier
                      </div>
                    </div>
                  </div> */}
                </div>

                {/* Images Gallery */}
                {data.uploadedImages && data.uploadedImages.length > 0 && (
                  <div className="mt-6 border-t pt-6">
                    <h3 className="mb-4 font-semibold">Images ({data.uploadedImages.length})</h3>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      {data.uploadedImages.map((image, index) => (
                        <div key={image.id || index} className="relative">
                          <img
                            src={image.url}
                            alt={`Experience image ${index + 1}`}
                            className="h-24 w-full rounded-lg object-cover"
                          />
                          <div className="absolute bottom-1 left-1 rounded bg-black/50 px-1 py-0.5 text-xs text-white">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities & Add-ons */}
                {((amenitiesList && amenitiesList.length > 0) || (addonsList && addonsList.length > 0)) && (
                  <div className="mt-6 border-t pt-6">
                    <h3 className="mb-4 font-semibold">Amenities & Add-ons</h3>
                    <div className="space-y-4">
                      {amenitiesList && amenitiesList.length > 0 && (
                        <div>
                          <div className="mb-2 text-sm font-medium">Amenities:</div>
                          <div className="flex flex-wrap gap-2">
                            {amenitiesList.map(amenity => (
                              <span
                                key={amenity.id}
                                className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-700 dark:bg-blue-950/20 dark:text-blue-400"
                              >
                                {amenity.label || amenity.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {addonsList && addonsList.length > 0 && (
                        <div>
                          <div className="mb-2 text-sm font-medium">Add-ons:</div>
                          <div className="space-y-2">
                            {addonsList.map(addon => (
                              <div
                                key={addon.id}
                                className="flex items-center justify-between rounded-lg border bg-background p-3"
                              >
                                <div>
                                  <div className="font-semibold text-sm">{addon.name}</div>
                                  {addon.description && (
                                    <div className="text-xs text-muted-foreground">{addon.description}</div>
                                  )}
                                </div>
                                {addon.unitPrice && (
                                  <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                    {formatPrice(addon.unitPrice, addon.duration)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Policies */}
                {(selectedPolicy || (selectedTerms && selectedTerms.length > 0)) && (
                  <div className="mt-6 border-t pt-6">
                    <h3 className="mb-4 font-semibold">Policies</h3>
                    <div className="space-y-3">
                      {selectedPolicy && (
                        <div className="rounded-lg border bg-background p-3">
                          <div className="text-sm font-semibold">Cancellation Policy</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedPolicy.name}
                            {selectedPolicy.description ? ` — ${selectedPolicy.description}` : ""}
                          </div>
                        </div>
                      )}
                      {selectedTerms && selectedTerms.length > 0 && (
                        <div className="rounded-lg border bg-background p-3">
                          <div className="text-sm font-semibold">Terms & Conditions</div>
                          <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
                            {selectedTerms.map(t => (
                              <li key={t.id}>{t.title}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Completion Status */}
            <div className="rounded-lg border bg-card p-6">
              <div className="mb-4 flex items-center gap-2 text-green-600 dark:text-green-400">
                <Check className="h-5 w-5" />
                <h3 className="font-semibold">Service Completion</h3>
              </div>
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">100% Complete</span>
                  <span className="text-green-600 dark:text-green-400">4/4</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span>Basic Information</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span>Description & Media</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span>Ticket Pricing</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span>Cancellation Policy</span>
                </div>
              </div>
            </div>

            {/* Visibility Status */}
            <div className="rounded-lg border bg-card p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold">Visibility</h3>
                <div
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    data.visibility === "PUBLIC" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.visibility === "PUBLIC" ? (
                    <>
                      <Eye className="w-3 h-3" /> Public
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3 h-3" /> Private
                    </>
                  )}
                </div>
              </div>

              <div className="mb-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="visibility-toggle" className="text-sm font-medium" {...({} as any)}>
                      Public Search
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {data.visibility === "PUBLIC" ? "Visible to all customers" : "Only visible to you"}
                    </p>
                  </div>
                  <Switch
                    id="visibility-toggle"
                    checked={data.visibility === "PUBLIC"}
                    onCheckedChange={(checked: boolean) => updateData({ visibility: checked ? "PUBLIC" : "PRIVATE" })}
                    {...({} as any)}
                  />
                </div>

                {/* Visible To - only reveal when visibility is public */}
                {data.visibility === "PUBLIC" && (
                  <div className="border-t pt-4">
                    <Label className="flex items-center text-sm font-medium" {...({} as any)}>
                      Visible To
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Choose which customer types can see and book this service when it's set to Public.
                    </p>
                    <div className="space-y-4 mt-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm">Corporate customers</p>
                          <p className="text-xs text-muted-foreground">Show on corporate dashboard &amp; booking</p>
                        </div>
                        <Switch
                          checked={data.visibleToCorporate ?? false}
                          onCheckedChange={(checked: boolean) => updateData({ visibleToCorporate: checked })}
                          {...({} as any)}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm">Consumer customers</p>
                          <p className="text-xs text-muted-foreground">Show on consumer marketplace &amp; booking</p>
                        </div>
                        <Switch
                          checked={data.visibleToConsumer ?? false}
                          onCheckedChange={(checked: boolean) => updateData({ visibleToConsumer: checked })}
                          {...({} as any)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {isEditMode && (
                  <Button
                    onClick={onTogglePublish}
                    variant="outline"
                    className={`w-full gap-2 ${
                      data.status === "PUBLISHED"
                        ? "border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-900/50 dark:text-orange-400 dark:hover:bg-orange-900/20"
                        : "border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700 dark:border-green-900/50 dark:text-green-400 dark:hover:bg-green-900/20"
                    }`}
                    disabled={publishing}
                    {...({} as any)}
                  >
                    <Rocket className="h-4 w-4" />
                    {publishing
                      ? data.status === "PUBLISHED"
                        ? "Unpublishing..."
                        : "Publishing..."
                      : data.status === "PUBLISHED"
                        ? "Unpublish"
                        : "Publish"}
                  </Button>
                )}
              </div>
              {!isEditMode && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Setting visibility to private will hide this service from the public marketplace.
                </p>
              )}
            </div>

            {/* Success Tips */}
            <div className="rounded-lg border bg-card p-6">
              <h3 className="mb-4 font-semibold">Success Tips</h3>
              <div className="space-y-4">
                <div className="rounded-md bg-green-50 p-3 dark:bg-green-950/20">
                  <p className="mb-1 text-sm font-semibold text-green-900 dark:text-green-100">🎉 After Publishing</p>
                  <p className="text-xs text-green-800 dark:text-green-200">
                    Share your play service on social media and with friends to get your first bookings
                  </p>
                </div>
                <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-950/20">
                  <p className="mb-1 text-sm font-semibold text-yellow-900 dark:text-yellow-100">📸 Photo Tips</p>
                  <p className="text-xs text-yellow-800 dark:text-yellow-200">
                    Add more photos later to increase bookings by up to 40%
                  </p>
                </div>
                <div className="rounded-md bg-purple-50 p-3 dark:bg-purple-950/20">
                  <p className="mb-1 text-sm font-semibold text-purple-900 dark:text-purple-100">⭐ Reviews</p>
                  <p className="text-xs text-purple-800 dark:text-purple-200">
                    Great reviews boost visibility. Ask happy participants to leave feedback
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="mt-8 flex items-center justify-between border-t pt-8">
          <Button variant="outline" onClick={onPrev} className="gap-2 bg-transparent" {...({} as any)}>
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="flex gap-2">
            {isEditMode && (
              <Button
                onClick={onTogglePublish}
                variant="outline"
                className={`gap-2 ${
                  data.status === "PUBLISHED"
                    ? "border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                    : "border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700"
                }`}
                disabled={publishing}
                {...({} as any)}
              >
                <Rocket className="h-4 w-4" />
                {publishing
                  ? data.status === "PUBLISHED"
                    ? "Unpublishing..."
                    : "Publishing..."
                  : data.status === "PUBLISHED"
                    ? "Unpublish"
                    : "Publish"}
              </Button>
            )}
            <Button
              onClick={handlePublish}
              size="lg"
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              disabled={publishing}
              {...({} as any)}
            >
              {publishing
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                  ? "Update Service"
                  : "Create Play Service"}
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-2">Delete Play Service?</h3>
              <p className="text-sm text-muted-foreground mb-6">
                This action cannot be undone. This will permanently delete the play service.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  {...({} as any)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={deleting}
                  {...({} as any)}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
